/*@jsxRuntime automatic @jsxImportSource react*/
/*<!-- vale Hightouch.Substitutions = NO -->*/
/*<!-- vale Hightouch.Substitutions  = YES -->*/
/*<!-- vale Google.Headings = NO -->*/
/*<!-- vale Google.Headings  = YES -->*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1792540800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Oct 21 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "mongodb",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to MongoDB",
          "depth": 0,
          "id": "connect-to-mongodb"
        }, {
          "value": "SSH tunneling",
          "depth": 1,
          "id": "ssh-tunneling"
        }, {
          "value": "Retrieve MongoDB credentials",
          "depth": 1,
          "id": "retrieve-mongodb-credentials"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Delete behavior",
          "depth": 1,
          "id": "delete-behavior"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Best practices",
          "depth": 1,
          "id": "best-practices"
        }, {
          "value": "Compatible versions",
          "depth": 1,
          "id": "compatible-versions"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      strong: "strong",
      h3: "h3",
      code: "code",
      img: "img",
      h4: "h4",
      ul: "ul",
      li: "li",
      ol: "ol"
    }, _provideComponents(), props.components), {CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "The MongoDB destination combines the analytical power of your data warehouse with the low-latency performance of a transactional database. It's been battle-tested with syncs up to hundreds of millions of rows."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Documents"
            }), _jsx(_components.td, {
              children: "Sync data to documents in a MongoDB collection"
            }), _jsx(_components.td, {
              children: "Upsert"
            })]
          })
        })]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-mongodb",
        children: "Connect to MongoDB"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Make sure to allowlist Hightouch IP addresses so Hightouch can reach your cluster. Refer to our\n", _jsx(_components.a, {
            href: "/security/networking#ip-addresses",
            children: "IP address docs"
          }), " to find the relevant IP addresses for your Hightouch region. If you are using Atlas, you can add allowlist IP addresses from ", _jsx(_components.strong, {
            children: "Security > Network Access"
          }), "."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["When creating a MongoDB destination, you can either enter the ", _jsx(_components.strong, {
          children: "Host"
        }), ", ", _jsx(_components.strong, {
          children: "Port"
        }), ", ", _jsx(_components.strong, {
          children: "Database"
        }), ", ", _jsx(_components.strong, {
          children: "User"
        }), ", and ", _jsx(_components.strong, {
          children: "Password"
        }), ", or you can provide the full URI connection string. For more information, go to the ", _jsx(_components.a, {
          href: "https://docs.mongodb.com/drivers/node/current/fundamentals/connection/",
          children: "official connection guide"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "ssh-tunneling",
        children: "SSH tunneling"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch can connect directly to MongoDB over the public internet or via an SSH tunnel. Since data is encrypted in transit via TLS, a direct connection is suitable for most use cases. You may need to set up a tunnel if your MongoDB instance is on a private network or virtual private cloud (VPC)."
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports both standard and reverse SSH tunnels. To learn more about SSH tunneling, refer to Hightouch's ", _jsx(_components.a, {
          href: "/security/ssh-tunneling",
          children: "tunneling documentation"
        }), "."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Through an SSH tunnel, Hightouch connects directly to the single MongoDB host\nand port the tunnel points to. It doesn't use seedlist (SRV) records or\ndiscover other replica set members, and it ignores the ", _jsx(_components.strong, {
            children: "Is the host name a\nseedlist record?"
          }), " setting. If you use MongoDB Atlas, point the tunnel at a\nspecific node's host name, not a ", _jsx(_components.code, {
            children: "mongodb+srv"
          }), " host name."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "retrieve-mongodb-credentials",
        children: "Retrieve MongoDB credentials"
      }), "\n", _jsxs(_components.p, {
        children: ["Assign the user whose credentials you use the built-in ", _jsx(_components.a, {
          href: "https://www.mongodb.com/docs/atlas/security-add-mongodb-users/#mongodb-atlasrole-Read-and-write-to-any-database",
          children: _jsx(_components.code, {
            children: "readWriteAnyDatabase"
          })
        }), " MongoDB role or create a ", _jsx(_components.a, {
          href: "https://www.mongodb.com/docs/atlas/security-add-mongodb-roles/",
          children: "custom role"
        }), " and assign these actions at minimum:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Action"
            }), _jsx(_components.th, {
              align: "left",
              children: "Details"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.a, {
                href: "https://www.mongodb.com/docs/manual/reference/privilege-actions/#mongodb-authaction-insert",
                children: _jsx(_components.code, {
                  children: "insert"
                })
              })
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Grants permission to perform ", _jsx(_components.code, {
                children: "insert"
              }), " and ", _jsx(_components.code, {
                children: "create"
              }), " commands"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.a, {
                href: "https://www.mongodb.com/docs/manual/reference/privilege-actions/#mongodb-authaction-remove",
                children: _jsx(_components.code, {
                  children: "remove"
                })
              })
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Grants permission to perform the ", _jsx(_components.code, {
                children: "delete"
              }), " command"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.a, {
                href: "https://www.mongodb.com/docs/manual/reference/privilege-actions/#mongodb-authaction-update",
                children: _jsx(_components.code, {
                  children: "update"
                })
              })
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Grants permission to perform the ", _jsx(_components.code, {
                children: "update"
              }), " command"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.a, {
                href: "https://www.mongodb.com/docs/manual/reference/privilege-actions/#mongodb-authaction-listIndexes",
                children: _jsx(_components.code, {
                  children: "listIndexes"
                })
              })
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Grants permission to perform the ", _jsx(_components.code, {
                children: "listIndexes"
              }), " command"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.a, {
                href: "https://www.mongodb.com/docs/manual/reference/privilege-actions/#mongodb-authaction-listCollections",
                children: _jsx(_components.code, {
                  children: "listCollections"
                })
              })
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Grants permission to perform the ", _jsx(_components.code, {
                children: "listCollections"
              }), " command"]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-mongo-custom-role.png",
          alt: "Custom Role actions"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["To create a user in MongoDB, use ", _jsx(_components.a, {
          href: "https://www.mongodb.com/docs/manual/reference/method/db.createUser/",
          children: _jsx(_components.code, {
            children: "db.createUser()"
          })
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "authenticate-with-connection-parameters",
        children: "Authenticate with connection parameters"
      }), "\n", _jsx(_components.p, {
        children: "Enter the following required fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Host"
          }), ": The hostname or IP address of your MongoDB server. For help finding this, go to the ", _jsx(_components.a, {
            href: "https://www.mongodb.com/docs/drivers/node/current/fundamentals/connection/connect/#std-label-node-connect-to-mongodb",
            children: "official connection guide"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Port"
          }), ": The port number of your MongoDB server. The default port number is 27017, but yours may be different. You don't need to specify the port if you are using SRV protocol."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Database"
          }), ": The name of the your MongoDB database."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Username"
          }), ": This can be your personal MongoDB login or a dedicated user for Hightouch."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Password"
          }), ": This is the password for the user specified above. If you're using ", _jsx(_components.a, {
            href: "https://www.mongodb.com/atlas/database",
            children: "Atlas"
          }), ", the user password might differ from the one you use to login to your MongoDB Atlas instance."]
        }), "\n"]
      }), "\n", "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Is the host name a seedlist record?"
          }), ": To use ", _jsx(_components.a, {
            href: "https://www.mongodb.com/docs/manual/reference/connection-string/#std-label-connections-dns-seedlist",
            children: "SRV"
          }), " protocol (Hightouch defaults to ", _jsx(_components.a, {
            href: "https://www.mongodb.com/docs/manual/reference/connection-string/#std-label-connections-standard-connection-string-format",
            children: "standard"
          }), "), you must toggle this setting. If you are using MongoDB Atlas and the connection string begins with protocol ", _jsx(_components.code, {
            children: "mongodb+srv"
          }), ", toggle this setting to connect."]
        }), "\n"]
      }), "\n", "\n", _jsx(_components.h4, {
        id: "authenticate-with-connection-string",
        children: "Authenticate with connection string"
      }), "\n", _jsx(_components.p, {
        children: "The connection string is available in multiple locations. Follow these instructions for one way to find it."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["From the ", _jsx(_components.a, {
              href: "https://cloud.mongodb.com/v2/",
              children: "MongoDB UI"
            }), " click the ", _jsx(_components.strong, {
              children: "Connect"
            }), " button next to the name of your cluster:\n", _jsx(_components.img, {
              src: "destinations/destination-mongo-connect-button.png",
              alt: "Connect button in MongoDB UI"
            })]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["From the options displayed, select ", _jsx(_components.strong, {
              children: "Connect your application"
            }), ":\n", _jsx(_components.img, {
              src: "destinations/destination-mongo-connect-app.png",
              alt: "Connection options in MongoDB UI"
            })]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Follow the instructions for changing the password and database name in the string. Copy the string by clicking the copy icon:\n", _jsx(_components.img, {
          src: "destinations/destination-mongo-save-connection-string.png",
          alt: "Connection string in the MongoDB UI"
        })]
      }), "\n", _jsx(_components.p, {
        children: "In Hightouch, in the MongoDB setup configuration, paste your saved connection string into the provided field."
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch requires choosing one column to define how to match data in your source to documents in MongoDB. It's usually best to use the ", _jsxs(_components.a, {
          href: "https://www.mongodb.com/docs/manual/core/document/#the-_id-field",
          children: [_jsx(_components.code, {
            children: "_id"
          }), " field"]
        }), " in MongoDB. Otherwise, you can also select ", _jsx(_components.code, {
          children: "_id (Object ID)"
        }), " if you already have ", _jsx(_components.a, {
          href: "https://www.mongodb.com/docs/manual/reference/bson-types/#objectid",
          children: "Object ID"
        }), " values in your source data."]
      }), "\n", _jsxs(_components.p, {
        children: ["You can also choose to record match on a custom MongoDB field. To do this, you need to create a ", _jsx(_components.a, {
          href: "https://www.mongodb.com/docs/manual/core/index-unique/#unique-index-on-a-single-field",
          children: "unique index on a single field"
        }), ", which is a specific type of ", _jsx(_components.a, {
          href: "https://www.mongodb.com/docs/upcoming/core/indexes/index-types/index-single/#single-field-indexes",
          children: "single field index"
        }), ". Before creating this index, make sure that the field doesn't contain duplicate values, as this makes the index build fail with an ", _jsx(_components.code, {
          children: "Unknown error"
        }), " message."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["If you record match on a custom field, MongoDB automatically generates an Object ID to insert in the ", _jsx(_components.code, {
            children: "_id"
          }), " field."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["You can map the columns you want to include when creating new MongoDB documents or updating existing ones by selecting the model column and typing the respective MongoDB field name. Otherwise, you can include every column from your model by checking the ", _jsx(_components.strong, {
          children: "Sync all columns"
        }), " box."]
      }), "\n", _jsx(_components.h3, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsx(_components.p, {
        children: "You can choose how to handle documents in MongoDB when the corresponding rows are deleted in your source."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the document in MongoDB"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Clear Fields"
              })
            }), _jsx(_components.td, {
              children: "Clear the mapped fields but keep the document"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete Destination Record"
              })
            }), _jsx(_components.td, {
              children: "Delete the MongoDB document completely"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "best-practices",
        children: "Best practices"
      }), "\n", _jsx(_components.p, {
        children: "Create a user in MongoDB specifically for Hightouch and only allow that user to access the collections you want Hightouch to access. Don't use the 'root' user."
      }), "\n", _jsx(_components.h3, {
        id: "compatible-versions",
        children: "Compatible versions"
      }), "\n", _jsxs(_components.p, {
        children: ["The minimum supported MongoDB version is ", _jsx(_components.code, {
          children: "4.0"
        }), ". Earlier versions aren't officially supported but may work regardless."]
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", "\n", _jsx(_components.h4, {
        id: "mongoservererror-bad-auth-authentication-failed",
        children: "MongoServerError: bad auth: Authentication failed"
      }), "\n", "\n", _jsxs(_components.p, {
        children: ["This error can occur if the destination wasn't ", _jsx(_components.a, {
          href: "#authenticate-with-connection-parameters",
          children: "configured correctly"
        }), ". Make sure to use the correct password for the user specified in the configuration. If the error is still happening, try changing your MongoDB user's password and attempting the setup again."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
